/**
 * Bulk reorder hook for tree surfaces.
 *
 * Posts a {kind, items[{id, sortOrder}]} payload to the shared
 * /reorder endpoint and invalidates the relevant React Query caches
 * so the list re-fetches with the new order.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type ReorderKind = 'templates' | 'reports' | 'dashboards' | 'folders';

export interface ReorderItem {
  id: string;
  sortOrder: number;
}

const INVALIDATION_KEY_BY_KIND: Record<ReorderKind, ReadonlyArray<string>> = {
  templates: ['report-templates'],
  reports: ['reports'],
  dashboards: ['dashboards'],
  folders: ['folders'],
};

export function useReorderTree() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, items }: { kind: ReorderKind; items: ReorderItem[] }) =>
      api.fetch<{ updated: number }>('/reorder', {
        method: 'POST',
        body: JSON.stringify({ kind, items }),
      }),
    onSuccess: async (_data, vars) => {
      await Promise.all(
        INVALIDATION_KEY_BY_KIND[vars.kind].map((key) => qc.invalidateQueries({ queryKey: [key] })),
      );
    },
  });
}

/**
 * Move an item up or down within its sibling list, then POST the
 * resulting order. Caller is responsible for supplying the siblings
 * array in the order it's currently displayed (top → bottom).
 */
export function useMoveItem() {
  const reorder = useReorderTree();
  return (
    kind: ReorderKind,
    itemId: string,
    direction: 'up' | 'down',
    siblings: ReadonlyArray<{ id: string }>,
  ): void => {
    const idx = siblings.findIndex((s) => s.id === itemId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= siblings.length) return;
    const arr = siblings.slice();
    const [moved] = arr.splice(idx, 1);
    if (!moved) return;
    arr.splice(newIdx, 0, moved);
    reorder.mutate({
      kind,
      items: arr.map((s, i) => ({ id: s.id, sortOrder: i + 1 })),
    });
  };
}
